import React, { useEffect, useMemo, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getLanguages, translateData } from "../lib/api";
import { RichTextEditor } from "./child/RichTextEditor";
import { toast } from "react-toastify";

const CONTENT_FIELDS = [
    ["CareToBeTaken", "Care to be taken"],
    ["SelectionPriorities", "Selection priorities"],
    ["EssentialManagement", "Essential management"],
    ["PriorityHealthModules", "Priority health modules"],
    ["ProductivityIndicators", "Productivity indicators"],
    ["ReproductionAndNewbornManagement", "Reproduction and newborn management"],
    ["VeterinaryMedicineAndFoodSafety", "Veterinary medicine and food safety"],
    [
        "VaccinationsDewormingDiseasesLaboratoryResultsAndTreatments",
        "Vaccinations, deworming, diseases, laboratory results and treatments",
    ],
    ["EmergencyAlerts", "Emergency alerts and outbreak reporting"],
];

// Helper to strip HTML tags to accurately validate empty fields (e.g. TipTap outputs "<p></p>")
const isRichTextEmpty = (htmlString) => {
    if (!htmlString) return true;
    const stripped = htmlString.replace(/<(.|\n)*?>/g, "").trim();
    return stripped.length === 0;
};

const languageCode = (language) => language?.GoogleLanguageCode || language?.LanguageCode || "";
const isEnglish = (language) => ["en", "en-us", "en-gb", "eng"].includes(languageCode(language).toLowerCase());
const getToken = () => localStorage.getItem("authToken") || localStorage.getItem("token");

const blankDetail = (language) => ({
    LanguageID: language?.ID || language?.LanguageID || "",
    LanguageCode: language?.LanguageCode || "",
    GoogleLanguageCode: languageCode(language),
    ...Object.fromEntries(CONTENT_FIELDS.map(([key]) => [key, ""])),
    PdfFileName: "",
    PdfSystemFileName: "",
});

const normalizeLanguages = (response) => {
    const list = response?.status === "success" ? response.data : response?.data || response;
    return (Array.isArray(list) ? list : []).filter((language) =>
        language.IsLanguageActivated === true || language.IsLanguageActivated === 1 ||
        language.IsActive === true || language.IsActive === 1 ||
        language.IsActivated === true || language.IsActivated === 1,
    );
};

const parseObject = (value) => {
    if (!value) return {};
    if (typeof value === "object") return value;
    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
};

const normalizeSpeciesTranslations = (value) => {
    const parsed = parseObject(value);
    return parsed.Species && typeof parsed.Species === "object" ? parsed.Species : parsed;
};

const normalizeDetails = (livestock, languages) => {
    const current = {};
    const sourceDetails = livestock?.LivestockDetails || livestock?.livestockDetails || [];
    languages.forEach((language) => {
        const code = languageCode(language);
        const existing = sourceDetails.find((detail) =>
            (detail.GoogleLanguageCode || detail.LanguageCode) === code,
        );
        current[code] = existing ? { ...blankDetail(language), ...existing } : blankDetail(language);
    });
    return current;
};

const fileUrl = (folder, fileName) =>
    fileName ? `https://api.soksan-dss.com/uploads/${folder}/${fileName}` : "";

const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const units = ["Bytes", "KB", "MB", "GB"];
    const index = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, index)).toFixed(2))} ${units[index]}`;
};

export default function AddLivestock({ isOpen, onClose, onSubmit, livestockToEdit }) {
    const [languages, setLanguages] = useState([]);
    const [details, setDetails] = useState({});
    const [species, setSpecies] = useState("");
    const [speciesTranslations, setSpeciesTranslations] = useState({});
    const [files, setFiles] = useState({});
    const [isActive, setIsActive] = useState(true);
    const [selectedCode, setSelectedCode] = useState("");
    const [mainTab, setMainTab] = useState("english");
    const [translationCompleted, setTranslationCompleted] = useState(false);
    const [loadingLanguages, setLoadingLanguages] = useState(false);
    const [saving, setSaving] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    // Master feature image state
    const [masterFeatureImage, setMasterFeatureImage] = useState(null);
    const [masterFeatureImagePreview, setMasterFeatureImagePreview] = useState("");

    // Other Images state (Max 10)
    const [otherImages, setOtherImages] = useState([]);

    // Video state (Max 1)
    const [video, setVideo] = useState(null);
    const [videoName, setVideoName] = useState("");
    const [videoPreview, setVideoPreview] = useState("");

    const englishLanguage = useMemo(() => languages.find(isEnglish) || languages[0], [languages]);
    const englishCode = languageCode(englishLanguage);
    const otherLanguages = useMemo(() => languages.filter((language) => !isEnglish(language)), [languages]);
    const activeLanguage = languages.find((language) => languageCode(language) === selectedCode) || englishLanguage;
    const activeDetail = details[selectedCode] || {};
    const isUpdate = Boolean(livestockToEdit?.ID);
    const canOpenOtherLanguages = isUpdate || translationCompleted;

    useEffect(() => {
        if (!isOpen) return undefined;
        document.body.style.overflow = "hidden";
        let cancelled = false;
        setLoadingLanguages(true);
        setHasAttemptedSubmit(false);

        (async () => {
            try {
                const response = await getLanguages();
                const active = normalizeLanguages(response);
                if (cancelled) return;
                const english = active.find(isEnglish) || active[0];
                setLanguages(active);
                setDetails(normalizeDetails(livestockToEdit, active));
                setSelectedCode(languageCode(english));
                setSpecies(livestockToEdit?.Species || "");
                setSpeciesTranslations(normalizeSpeciesTranslations(livestockToEdit?.SpeciesOtherLang));
                setIsActive(livestockToEdit?.IsActive === undefined ? true : Boolean(livestockToEdit.IsActive));
                setFiles({});
                setMainTab("english");
                setTranslationCompleted(false);

                // Set master feature image if editing
                if (livestockToEdit?.FeatureImageSystemFileName) {
                    setMasterFeatureImagePreview(fileUrl("livestock/images", livestockToEdit.FeatureImageSystemFileName));
                    setMasterFeatureImage({
                        originalName: livestockToEdit.FeatureImageFileName,
                        systemName: livestockToEdit.FeatureImageSystemFileName,
                    });
                } else {
                    setMasterFeatureImage(null);
                    setMasterFeatureImagePreview("");
                }

                // Set other images if editing
                if (livestockToEdit?.OtherImages) {
                    let rawOther = livestockToEdit.OtherImages;
                    if (typeof rawOther === "string") {
                        try {
                            rawOther = JSON.parse(rawOther);
                        } catch {
                            rawOther = [rawOther];
                        }
                    }
                    if (Array.isArray(rawOther)) {
                        setOtherImages(
                            rawOther.map((imgName, idx) => ({
                                id: `existing-${idx}`,
                                name: imgName,
                                file: null,
                                preview: fileUrl("livestock/other_images", imgName),
                            }))
                        );
                    } else {
                        setOtherImages([]);
                    }
                } else {
                    setOtherImages([]);
                }

                // Set video if editing
                if (livestockToEdit?.Video) {
                    setVideo(livestockToEdit.Video);
                    setVideoName(livestockToEdit.Video);
                    setVideoPreview(fileUrl("livestock/videos", livestockToEdit.Video));
                } else {
                    setVideo(null);
                    setVideoName("");
                    setVideoPreview("");
                }
            } catch (error) {
                toast.error(error?.response?.data?.message || "Failed to load languages");
            } finally {
                if (!cancelled) setLoadingLanguages(false);
            }
        })();

        return () => {
            cancelled = true;
            document.body.style.overflow = "auto";
        };
    }, [isOpen, livestockToEdit]);

    const setDetailValue = (code, key, value) => {
        setDetails((previous) => ({
            ...previous,
            [code]: { ...(previous[code] || {}), [key]: value },
        }));
    };

    const handleMasterFeatureImage = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File size should be less than 10MB");
            return;
        }
        setMasterFeatureImage(file);
        setMasterFeatureImagePreview(URL.createObjectURL(file));
    };

    const removeMasterFeatureImage = () => {
        setMasterFeatureImage(null);
        setMasterFeatureImagePreview("");
    };

    const handleOtherImages = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (!selectedFiles.length) return;

        if (otherImages.length + selectedFiles.length > 10) {
            toast.error(`Maximum 10 images are allowed. You can only add ${10 - otherImages.length} more.`);
            return;
        }

        const validNewImages = [];
        for (const file of selectedFiles) {
            if (!file.type.startsWith("image/")) {
                toast.error(`${file.name} is not a valid image file`);
                continue;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error(`${file.name} exceeds 10MB limit`);
                continue;
            }
            validNewImages.push({
                id: `${Date.now()}-${Math.random()}`,
                name: file.name,
                file,
                preview: URL.createObjectURL(file),
            });
        }

        if (validNewImages.length > 0) {
            setOtherImages((prev) => [...prev, ...validNewImages]);
        }
        event.target.value = "";
    };

    const removeOtherImage = (indexToRemove) => {
        setOtherImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleVideo = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("video/")) {
            toast.error("Please upload a valid video file");
            return;
        }
        if (file.size > 100 * 1024 * 1024) {
            toast.error("Video file size should be less than 100MB");
            return;
        }

        setVideo(file);
        setVideoName(file.name);
        setVideoPreview(URL.createObjectURL(file));
        event.target.value = "";
    };

    const removeVideo = () => {
        setVideo(null);
        setVideoName("");
        setVideoPreview("");
    };

    const handleFile = (code, type, file) => {
        if (!file) return;
        if (type === "pdf" && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Please upload a PDF file");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File size should be less than 10MB");
            return;
        }
        setFiles((previous) => ({
            ...previous,
            [code]: {
                ...(previous[code] || {}),
                [type]: file,
            },
        }));
        setDetailValue(code, type === "image" ? "FeatureImageFileName" : "PdfFileName", file.name);
    };

    const removeFile = (code, type) => {
        setFiles((previous) => {
            const next = { ...previous, [code]: { ...(previous[code] || {}) } };
            delete next[code][type];
            return next;
        });
        setDetailValue(code, type === "image" ? "FeatureImageFileName" : "PdfFileName", "");
        setDetailValue(code, type === "image" ? "FeatureImageSystemFileName" : "PdfSystemFileName", "");
    };

    const translationValue = (response, key, code, language) =>
        response?.[key]?.[code] || response?.[key]?.[language?.LanguageCode] || response?.[key]?.en || "";

    const translateEnglish = async () => {
        setHasAttemptedSubmit(true);
        const english = details[englishCode] || {};
        const missingContent = CONTENT_FIELDS.some(([key]) => isRichTextEmpty(english[key]));
        if (!species.trim() || missingContent) {
            toast.error("Complete the English species and content fields before translating");
            return;
        }
        setTranslating(true);
        try {
            const source = {
                Species: species.trim(),
                ...Object.fromEntries(CONTENT_FIELDS.map(([key]) => [key, english[key] || ""])),
            };
            const result = await translateData(source, getToken());
            const response = result?.data?.data || result?.data || result || {};
            const nextSpecies = { ...speciesTranslations };
            const nextDetails = { ...details };
            languages.forEach((language) => {
                const code = languageCode(language);
                if (isEnglish(language)) return;
                nextSpecies[code] = translationValue(response, "Species", code, language);
                nextDetails[code] = {
                    ...(nextDetails[code] || blankDetail(language)),
                    ...Object.fromEntries(CONTENT_FIELDS.map(([key]) => [key, translationValue(response, key, code, language)])),
                };
            });
            setSpeciesTranslations(nextSpecies);
            setDetails(nextDetails);
            setTranslationCompleted(true);
            setHasAttemptedSubmit(false);
            setMainTab("other");
            setSelectedCode(languageCode(otherLanguages[0] || englishLanguage));
            toast.success("Translations generated successfully");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Translation failed");
        } finally {
            setTranslating(false);
        }
    };

    const buildFormData = () => {
        const missingDetails = languages.filter((language) => {
            const detail = details[languageCode(language)] || {};
            return CONTENT_FIELDS.some(([key]) => isRichTextEmpty(detail[key]));
        });
        if (missingDetails.length > 0) {
            throw new Error(`Please complete all fields for: ${missingDetails.map((language) => language.LanguageName || languageCode(language)).join(", ")}`);
        }
        const missingSpecies = otherLanguages.filter((language) => !String(speciesTranslations[languageCode(language)] || "").trim());
        if (missingSpecies.length > 0) {
            throw new Error(`Please enter species name for: ${missingSpecies.map((language) => language.LanguageName || languageCode(language)).join(", ")}`);
        }

        const pdfUploadName = (code, file) => (file ? `${code}-${file.name}` : "");
        const detailsPayload = languages.map((language) => {
            const code = languageCode(language);
            const detail = details[code] || blankDetail(language);
            const selectedFiles = files[code] || {};
            return {
                LanguageID: detail.LanguageID || language.ID || language.LanguageID,
                LanguageCode: language.LanguageCode,
                GoogleLanguageCode: code,
                ...Object.fromEntries(CONTENT_FIELDS.map(([key]) => [key, detail[key] || ""])),
                PdfOriginalName: selectedFiles.pdf
                    ? pdfUploadName(code, selectedFiles.pdf)
                    : detail.PdfFileName || "",
            };
        });

        const formData = new FormData();
        formData.append("Species", species.trim());
        formData.append("SpeciesOtherLang", JSON.stringify(speciesTranslations));
        formData.append("IsActive", String(isActive));
        formData.append("LivestockDetails", JSON.stringify(detailsPayload));

        if (masterFeatureImage instanceof File) {
            formData.append("FeatureImages", masterFeatureImage);
        }

        // Handle OtherImages
        const existingOtherNames = [];
        otherImages.forEach((img) => {
            if (img.file instanceof File) {
                formData.append("OtherImages", img.file);
            } else if (img.name) {
                existingOtherNames.push(img.name);
            }
        });
        if (existingOtherNames.length > 0) {
            formData.append("OtherImages", JSON.stringify(existingOtherNames));
        } else if (isUpdate && otherImages.length === 0) {
            formData.append("OtherImages", JSON.stringify([]));
        }

        // Handle Video
        if (video instanceof File) {
            formData.append("Video", video);
        } else if (typeof video === "string" && video) {
            formData.append("Video", video);
        } else if (isUpdate && !video) {
            formData.append("Video", "");
        }

        Object.entries(files).forEach(([code, languageFiles]) => {
            if (languageFiles?.pdf) formData.append("PdfFiles", languageFiles.pdf, pdfUploadName(code, languageFiles.pdf));
        });
        return formData;
    };

    const handlePrimaryAction = async (event) => {
        event.preventDefault();
        setHasAttemptedSubmit(true);

        if (!translationCompleted) {
            await translateEnglish();
            return;
        }
        setSaving(true);
        try {
            const formData = buildFormData();
            await onSubmit(formData, isUpdate, livestockToEdit?.ID);
            onClose();
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Failed to save livestock");
        } finally {
            setSaving(false);
        }
    };

    const renderMasterFeatureImage = () => (
        <div className="row g-3 mb-20">
            <div className="col-12">
                <label className="form-label">Feature image (Master)</label>
                {masterFeatureImagePreview ? (
                    <div className="media-preview">
                        <img src={masterFeatureImagePreview} alt="Livestock feature" />
                        <div className="media-file-info">
                            <span>{masterFeatureImage?.originalName || livestockToEdit?.FeatureImageFileName || "Image"}</span>
                            <button type="button" className="remove-media-btn" onClick={removeMasterFeatureImage}>×</button>
                        </div>
                    </div>
                ) : (
                    <label className="media-upload" htmlFor="master-feature-image">
                        <Icon icon="solar:gallery-add-outline" className="upload-icon" />
                        <span>Click to upload feature image</span>
                        <small>JPG, PNG, WEBP (Max 10MB)</small>
                    </label>
                )}
                <input
                    id="master-feature-image"
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(event) => handleMasterFeatureImage(event.target.files?.[0])}
                />
            </div>
        </div>
    );

    const renderOtherImages = () => (
        <div className="row g-3 mb-20">
            <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label mb-0">Other Images (Max 10)</label>
                    <span className="text-xs text-secondary-light">{otherImages.length}/10 images</span>
                </div>

                {otherImages.length > 0 && (
                    <div className="row g-2 mb-2">
                        {otherImages.map((img, index) => (
                            <div key={img.id || index} className="col-6 col-sm-4 col-md-3">
                                <div className="media-preview position-relative">
                                    <img src={img.preview} alt={`Other preview ${index + 1}`} />
                                    <button
                                        type="button"
                                        className="remove-media-btn position-absolute top-0 end-0 m-1"
                                        style={{
                                            background: "rgba(220, 53, 69, 0.9)",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            width: "22px",
                                            height: "22px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            lineHeight: 1,
                                            border: 0,
                                        }}
                                        onClick={() => removeOtherImage(index)}
                                    >
                                        ×
                                    </button>
                                    <div className="media-file-info">
                                        <span className="text-xs">{img.name || `Image ${index + 1}`}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {otherImages.length < 10 && (
                    <>
                        <label className="media-upload" htmlFor="master-other-images">
                            <Icon icon="solar:gallery-wide-outline" className="upload-icon" />
                            <span>Click to upload additional images</span>
                            <small>JPG, PNG, WEBP (Max 10MB per image, up to 10 images total)</small>
                        </label>
                        <input
                            id="master-other-images"
                            hidden
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleOtherImages}
                        />
                    </>
                )}
            </div>
        </div>
    );

    const renderVideo = () => (
        <div className="row g-3 mb-20">
            <div className="col-12">
                <label className="form-label">Livestock Video (Max 1)</label>
                {videoPreview ? (
                    <div className="media-preview">
                        <video
                            src={videoPreview}
                            controls
                            style={{ width: "100%", maxHeight: "200px", borderRadius: "6px", background: "#000" }}
                        />
                        <div className="media-file-info">
                            <Icon icon="solar:videocamera-record-outline" />
                            <span>{videoName || (video instanceof File ? video.name : "Video file")}</span>
                            <button type="button" className="remove-media-btn" onClick={removeVideo}>×</button>
                        </div>
                    </div>
                ) : (
                    <label className="media-upload" htmlFor="master-video">
                        <Icon icon="solar:videocamera-record-outline" className="upload-icon" />
                        <span>Click to upload video</span>
                        <small>MP4, WEBM, MKV, MOV (Max 100MB, 1 video only)</small>
                    </label>
                )}
                <input
                    id="master-video"
                    hidden
                    type="file"
                    accept="video/*"
                    onChange={handleVideo}
                />
            </div>
        </div>
    );

    const renderMedia = (detail, code) => {
        const selectedFiles = files[code] || {};
        const pdfName = selectedFiles.pdf?.name || detail.PdfFileName;
        return (
            <div className="row g-3 mb-20">
                <div className="col-12">
                    <label className="form-label">PDF document {!isEnglish(activeLanguage) && `(${activeLanguage?.LanguageName || selectedCode})`}</label>
                    {pdfName ? (
                        <div className="pdf-preview">
                            <div className="pdf-file-info">
                                <Icon icon="solar:file-text-outline" />
                                <span>{pdfName}</span>
                                {detail.PdfSystemFileName && (
                                    <a href={fileUrl("livestock/pdfs", detail.PdfSystemFileName)} target="_blank" rel="noreferrer">Open</a>
                                )}
                                <button type="button" className="remove-media-btn" onClick={() => removeFile(code, "pdf")}>×</button>
                            </div>
                            {selectedFiles.pdf && <span className="file-size">{formatFileSize(selectedFiles.pdf.size)}</span>}
                        </div>
                    ) : (
                        <label className="media-upload" htmlFor={`livestock-pdf-${code}`}>
                            <Icon icon="solar:file-upload-outline" className="upload-icon" />
                            <span>Click to upload PDF</span>
                            <small>PDF (Max 10MB)</small>
                        </label>
                    )}
                    <input
                        id={`livestock-pdf-${code}`}
                        hidden
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => handleFile(code, "pdf", event.target.files?.[0])}
                    />
                </div>
            </div>
        );
    };

    const renderLanguageContent = () => {
        const isCurrentEnglish = isEnglish(activeLanguage);
        const speciesVal = isCurrentEnglish ? species : (speciesTranslations[selectedCode] || "");
        const isSpeciesMissing = hasAttemptedSubmit && !speciesVal.trim();

        return (
            <>
                <div className="row g-3 mb-20">
                    <div className="col-md-8">
                        <label className="form-label">Species name{!isCurrentEnglish && ` (${activeLanguage?.LanguageName || selectedCode})`} *</label>
                        {isCurrentEnglish ? (
                            <input
                                className="form-control"
                                value={species}
                                onChange={(event) => setSpecies(event.target.value)}
                                placeholder="Enter species name in English"
                            />
                        ) : (
                            <input
                                className="form-control"
                                value={speciesTranslations[selectedCode] || ""}
                                onChange={(event) => setSpeciesTranslations((previous) => ({ ...previous, [selectedCode]: event.target.value }))}
                                placeholder={`Enter species name in ${activeLanguage?.LanguageName || selectedCode}`}
                            />
                        )}
                        {isSpeciesMissing && (
                            <span className="error-message d-block mt-1">Species name is required</span>
                        )}
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Status</label>
                        <div className="form-check style-check d-flex align-items-center gap-8 mt-2">
                            <input
                                className="form-check-input radius-4 border border-neutral-400"
                                type="checkbox"
                                checked={isActive}
                                onChange={(event) => setIsActive(event.target.checked)}
                            />
                            <label className="form-label fw-medium text-primary-light text-sm mb-0">Active</label>
                        </div>
                    </div>
                </div>

                {isCurrentEnglish && (
                    <>
                        {renderMasterFeatureImage()}
                        {renderOtherImages()}
                        {renderVideo()}
                    </>
                )}

                <div className="row g-3">
                    {CONTENT_FIELDS.map(([key, label]) => {
                        const rawValue = activeDetail[key] || "";
                        const isEmpty = isRichTextEmpty(rawValue);

                        return (
                            <div className="col-12" key={key}>
                                <label className="form-label">
                                    {label}{!isCurrentEnglish && ` (${activeLanguage?.LanguageName || selectedCode})`} *
                                </label>
                                
                                <RichTextEditor
                                    value={rawValue}
                                    onChange={(content) => setDetailValue(selectedCode, key, content)}
                                    placeholder={`Enter ${label.toLowerCase()}`}
                                />

                                {hasAttemptedSubmit && isEmpty && (
                                    <span className="error-message d-block mt-1">
                                        {label} is required
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
                <hr className="my-4" />
                {renderMedia(activeDetail, selectedCode)}
            </>
        );
    };

    return (
        <Drawer open={isOpen} onClose={onClose} direction="right" size={980} className="bg-white">
            <style>{`
                .livestock-drawer{height:100vh;overflow-y:auto;padding:24px}
                .livestock-tabs{display:flex;gap:8px;flex-wrap:wrap;border-bottom:1px solid #e5e7eb;padding-bottom:12px;margin-bottom:20px}
                .livestock-tab{border:1px solid #e5e7eb;background:#f9fafb;border-radius:8px;padding:8px 14px;font-weight:600;cursor:pointer}
                .livestock-tab.active{background:#e8f0fe;border-color:#1a73e8;color:#1a73e8}
                .livestock-tab:disabled{cursor:not-allowed;opacity:.55}
                .media-upload{border:2px dashed #e2e8f0;border-radius:8px;padding:1rem;text-align:center;background:#f8fafc;cursor:pointer;min-height:110px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:#64748b}
                .media-upload:hover{background:#f1f5f9}
                .media-upload small{font-size:.75rem;color:#94a3b8}
                .upload-icon{font-size:24px;color:#64748b}
                .media-preview{padding:8px;background:#f8f9fa;border:1px solid #e5e7eb;border-radius:8px}
                .media-preview img{width:100%;height:110px;object-fit:contain;border-radius:6px;background:#fff}
                .media-file-info,.pdf-file-info{display:flex;align-items:center;gap:8px;margin-top:8px;font-size:.875rem}
                .media-file-info span,.pdf-file-info span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
                .remove-media-btn{margin-left:auto;background:none;border:0;color:#dc3545;cursor:pointer;font-size:1.2rem}
                .pdf-preview{padding:10px;background:#f8f9fa;border:1px solid #e5e7eb;border-radius:8px;min-height:110px}
                .file-size{font-size:.75rem;color:#6b7280}
                .error-message{color:#dc3545;font-size:0.75rem;}
            `}</style>
            <div className="livestock-drawer">
                <div className="d-flex justify-content-between align-items-center mb-20">
                    <h4 className="mb-0">{isUpdate ? "Update livestock" : "Add livestock"}</h4>
                    <button type="button" className="btn" onClick={onClose}>
                        <Icon icon="lucide:x" />
                    </button>
                </div>
                <div className="livestock-tabs">
                    <button
                        type="button"
                        className={`livestock-tab ${mainTab === "english" ? "active" : ""}`}
                        onClick={() => { setMainTab("english"); setSelectedCode(englishCode); }}
                    >
                        English
                    </button>
                    <button
                        type="button"
                        className={`livestock-tab ${mainTab === "other" ? "active" : ""}`}
                        disabled={!canOpenOtherLanguages}
                        title={!canOpenOtherLanguages ? "Translate English content first" : "Other languages"}
                        onClick={() => { if (!canOpenOtherLanguages) return; setMainTab("other"); setSelectedCode(languageCode(otherLanguages[0] || englishLanguage)); }}
                    >
                        Other languages
                    </button>
                </div>
                {mainTab === "other" && (
                    <div className="livestock-tabs">
                        {otherLanguages.map((language) => {
                            const code = languageCode(language);
                            return (
                                <button
                                    type="button"
                                    key={code}
                                    className={`livestock-tab ${selectedCode === code ? "active" : ""}`}
                                    onClick={() => setSelectedCode(code)}
                                >
                                    {language.LanguageName || language.LanguageCode}
                                </button>
                            );
                        })}
                    </div>
                )}
                {loadingLanguages ? (
                    <div className="text-center py-4">Loading languages...</div>
                ) : (
                    <form onSubmit={handlePrimaryAction}>
                        {renderLanguageContent()}
                        <div className="d-flex justify-content-end gap-3 mt-4 pb-4">
                            <button type="button" className="border border-danger-600 text-danger-600 px-56 py-10 radius-8" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-yellow px-56 py-12" disabled={saving || translating || languages.length === 0}>
                                {translating ? "Translating..." : saving ? (isUpdate ? "Updating..." : "Saving...") : !translationCompleted ? "Translate" : isUpdate ? "Update" : "Add livestock"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Drawer>
    );
}
